"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import Footer from "@/components/marketing/footer";

export default function FeaturesPage() {
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll("[data-animate]").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const show = (id: string) => visibleSections.has(id);

  const features = [
    {
      id: "clause-analysis",
      title: "Clause-by-Clause Analysis",
      image: "/images/features-analysis.webp",
      imageAlt: "Clause-by-clause contract analysis interface",
      layout: "image-left" as const,
      bullets: [
        "Every clause rated RED, YELLOW, or GREEN against market standards",
        "Deviation analysis with specific business impact assessment",
        "AI-generated redline suggestions ready to paste into the contract",
        "Deterministic severity rules \u2014 not subjective AI opinions",
        "Section header extraction with page number references",
      ],
    },
    {
      id: "versioning",
      title: "Document Versioning & Redline Comparison",
      image: "/images/features-versioning.webp",
      imageAlt: "Document versioning and redline comparison view",
      layout: "image-right" as const,
      bullets: [
        "Upload revised versions linked to originals",
        "Full-document text diff using diff-match-patch (not AI comparison)",
        "Red strikethrough for deletions, green underline for additions",
        "Download redline as DOCX for external review",
        "Version switcher \u2014 click to navigate between versions instantly",
        "\u201CShow Changes\u201D toggle highlights what\u2019s different across all tabs",
      ],
    },
  ];

  const obligationFeature = {
    id: "obligations",
    title: "Obligation Management & Calendar Integration",
    image: "/images/features-obligations.webp",
    imageAlt: "Obligation tracking and calendar integration",
    layout: "image-left" as const,
    bullets: [
      "Deadlines, responsible parties, recurring obligations extracted automatically",
      "Add custom obligations with full CRUD",
      "Due dates auto-calculated from priority (High=14d, Medium=30d, Low=60d)",
      "Override with date picker",
      "Export to Outlook/Google Calendar via ICS with RFC 5545 compliance",
      "RRULE recurrence (Daily through Annually)",
      "Rich calendar events with document name, project, version, classification, and direct URL back",
      "CSV export with effective dates for compliance tracking",
    ],
  };

  const playbookFeature = {
    id: "playbook",
    title: "Institutional Playbook",
    image: "/images/law-firms-library.webp",
    imageAlt: "Institutional playbook and knowledge management",
    layout: "image-right" as const,
    bullets: [
      "5 categories: Terminology, Position Standards, Risk Thresholds, Clause Templates, Regulatory Context",
      "Injected into every analysis automatically",
      "\u201CEnhance with AI\u201D uses your firm\u2019s templates, not generic market terms",
      "New associates produce partner-quality reviews from day one",
      "Contextual help explains each category",
    ],
  };

  return (
    <div className="min-h-screen bg-white text-gray-900" style={{ fontFamily: "'Georgia', 'Times New Roman', serif", scrollBehavior: "smooth" }}>
      <style>{`html { scroll-behavior: smooth; }`}</style>

      {/* --- NAV --- */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrollY > 40 ? "bg-white shadow-md py-1" : "bg-white/90 py-1.5"
      }`}>
        <div className="max-w-6xl mx-auto px-6 flex items-center justify-between">
          <Link href="/" className="flex items-center cursor-pointer">
            <img src="/logo.png" alt="Contract Lucidity" className="h-12 md:h-14" />
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link href="/features" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Features</Link>
            <Link href="/how-it-works" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>How It Works</Link>
            <Link href="/law-firms" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Law Firms</Link>
            <Link href="/in-house" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>In-House</Link>
            <Link href="/security" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Security</Link>
            <Link href="/why-contract-lucidity" className="text-sm text-gray-600 hover:text-[#1B3A6B] transition-colors" style={{ fontFamily: "system-ui, sans-serif" }}>Why Us</Link>
            <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" className="text-sm text-[#C8A84B] hover:text-[#b8993f] transition-colors font-semibold" style={{ fontFamily: "system-ui, sans-serif" }}>Lucid Vault</a>
            <Link
              href="/login"
              className="px-6 py-2.5 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-[#2E5EA0] transition-colors"
              style={{ fontFamily: "system-ui, sans-serif" }}
            >
              Sign In
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2" aria-label="Toggle menu">
            <svg className="w-6 h-6 text-[#1B3A6B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" style={{ fontFamily: "system-ui, sans-serif" }}>
            <div className="flex flex-col px-6 py-4 gap-3">
              <Link href="/features" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Features</Link>
              <Link href="/how-it-works" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">How It Works</Link>
              <Link href="/law-firms" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Law Firms</Link>
              <Link href="/in-house" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">In-House</Link>
              <Link href="/security" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Security</Link>
              <Link href="/why-contract-lucidity" onClick={() => setMobileMenuOpen(false)} className="text-sm text-gray-600 hover:text-[#1B3A6B] py-2 border-b border-gray-50">Why Us</Link>
              <a href="https://lucidvault.contractlucidity.com" target="_blank" rel="noopener noreferrer" onClick={() => setMobileMenuOpen(false)} className="text-sm text-[#C8A84B] font-semibold hover:text-[#b8993f] py-2 border-b border-gray-50">Lucid Vault</a>
              <Link href="/login" onClick={() => setMobileMenuOpen(false)} className="text-sm font-semibold text-white bg-[#1B3A6B] rounded px-4 py-2.5 text-center mt-2">Sign In</Link>
            </div>
          </div>
        )}
      </nav>

      {/* --- HERO --- */}
      <section className="pt-20">
        <div className="w-full overflow-hidden">
          <img src="/images/features-hero.webp" alt="Contract Lucidity features overview" className="w-full h-[300px] object-cover" loading="eager" />
        </div>
        <div className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-4xl md:text-5xl leading-[1.15] mb-6 text-[#1B3A6B]">
            Everything an attorney needs.<br />
            <span className="text-[#C8A84B]">Nothing they don&apos;t.</span>
          </h1>
          <p className="text-lg text-gray-500 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
            Six integrated capabilities that take a contract from upload to actionable output.
          </p>
        </div>
      </section>

      {/* --- FEATURE 1: Clause-by-Clause Analysis (image left) --- */}
      <section id={features[0].id} data-animate className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show(features[0].id) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <img src={features[0].image} alt={features[0].imageAlt} className="w-full h-72 object-cover" loading="lazy" />
            </div>
            <div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>01</p>
              <h2 className="text-2xl md:text-3xl text-[#1B3A6B] mb-6">{features[0].title}</h2>
              <ul className="space-y-3">
                {features[0].bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                    <span className="text-[#C8A84B] shrink-0 mt-0.5">&#9670;</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURE 2: Versioning & Redline (image right) --- */}
      <section id={features[1].id} data-animate className="py-20 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show(features[1].id) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="md:order-2 rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <img src={features[1].image} alt={features[1].imageAlt} className="w-full h-72 object-cover" loading="lazy" />
            </div>
            <div className="md:order-1">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>02</p>
              <h2 className="text-2xl md:text-3xl text-[#1B3A6B] mb-6">{features[1].title}</h2>
              <ul className="space-y-3">
                {features[1].bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                    <span className="text-[#C8A84B] shrink-0 mt-0.5">&#9670;</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURE 3: Version-Aware AI Pipeline (navy background, no image) --- */}
      <section id="ai-pipeline" data-animate className="py-20 bg-[#1B3A6B]">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("ai-pipeline") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="text-center mb-12">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>03</p>
              <h2 className="text-2xl md:text-3xl text-white mb-4">Version-Aware AI Pipeline</h2>
              <div className="inline-block h-px w-16 bg-[#C8A84B]" />
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {[
                "When a revision is uploaded, the AI doesn\u2019t start from scratch",
                "Runs the document diff first (programmatic, no AI)",
                "For each existing clause: \u201CIs this still valid?\u201D \u2014 boolean gate",
                "Unchanged clauses carry forward exactly (zero AI cost)",
                "Only re-evaluates clauses where the change is germane",
                "Executive summary, negotiation strategy, obligations all intelligently preserved",
              ].map((item, i) => (
                <div
                  key={i}
                  className={`flex gap-3 transition-all duration-700 ${show("ai-pipeline") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                  style={{ transitionDelay: `${i * 100}ms` }}
                >
                  <span className="text-[#C8A84B] shrink-0 mt-1 text-lg">&#9670;</span>
                  <p className="text-white/80 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURE 4: Obligation Management (image left) --- */}
      <section id={obligationFeature.id} data-animate className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show(obligationFeature.id) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <img src={obligationFeature.image} alt={obligationFeature.imageAlt} className="w-full h-72 object-cover" loading="lazy" />
            </div>
            <div>
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>04</p>
              <h2 className="text-2xl md:text-3xl text-[#1B3A6B] mb-6">{obligationFeature.title}</h2>
              <ul className="space-y-3">
                {obligationFeature.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                    <span className="text-[#C8A84B] shrink-0 mt-0.5">&#9670;</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURE 5: Cross-Document Intelligence (gold background) --- */}
      <section id="cross-doc" data-animate className="py-20 bg-[#C8A84B]">
        <div className="max-w-4xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("cross-doc") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 shadow-lg">
              <div className="text-center mb-8">
                <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>05</p>
                <h2 className="text-2xl md:text-3xl text-[#1B3A6B] mb-4">Cross-Document Intelligence</h2>
                <div className="inline-block h-px w-16 bg-[#C8A84B]" />
              </div>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  "Upload MSA, SOW, and NDA together",
                  "AI identifies conflicts between documents",
                  "Coverage gaps and alignment issues surfaced",
                  "No attorney has time to cross-reference 3 contracts manually",
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`flex gap-3 transition-all duration-700 ${show("cross-doc") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
                    style={{ transitionDelay: `${i * 100}ms` }}
                  >
                    <span className="text-[#1B3A6B] shrink-0 mt-1 text-lg">&#9670;</span>
                    <p className="text-gray-600 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURE 6: Institutional Playbook (image right) --- */}
      <section id={playbookFeature.id} data-animate className="py-20 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid md:grid-cols-2 gap-12 items-center transition-all duration-700 ${show(playbookFeature.id) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="md:order-2 rounded-xl overflow-hidden shadow-lg border border-gray-100">
              <img src={playbookFeature.image} alt={playbookFeature.imageAlt} className="w-full h-72 object-cover" loading="lazy" />
            </div>
            <div className="md:order-1">
              <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>06</p>
              <h2 className="text-2xl md:text-3xl text-[#1B3A6B] mb-6">{playbookFeature.title}</h2>
              <ul className="space-y-3">
                {playbookFeature.bullets.map((b, i) => (
                  <li key={i} className="flex gap-3 text-sm text-gray-500 leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>
                    <span className="text-[#C8A84B] shrink-0 mt-0.5">&#9670;</span>
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* --- FEATURE 7: Ethical Walls & Enterprise Security (navy background, no image) --- */}
      <section id="ethical-walls" data-animate className="py-20 bg-[#1B3A6B]">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("ethical-walls") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-sm tracking-[0.2em] uppercase text-[#C8A84B] mb-3" style={{ fontFamily: "system-ui, sans-serif" }}>07</p>
            <h2 className="text-2xl md:text-3xl text-white mb-4">Ethical Walls & Enterprise Security</h2>
            <p className="text-white/70 max-w-2xl mb-10" style={{ fontFamily: "system-ui, sans-serif" }}>
              Built for firms that handle matters on both sides. Information barriers that actually enforce.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { title: "Deny-Overrides-Allow", desc: "iManage-style enforcement. Wall members are blocked from walled projects regardless of any other access grants. No exceptions except seed admin." },
                { title: "Matter-Level Isolation", desc: "Assign walls to specific projects. Conflicts counsel configures walls, members, and covered matters from a single panel." },
                { title: "ABA Model Rule 1.10", desc: "Purpose-built for legal information barriers. Complete audit trail of every wall change, member addition, and access decision." },
                { title: "OIDC Single Sign-On", desc: "Connect your identity provider. Azure AD, Okta, Google Workspace. Dynamic redirect URLs. Auto-create users on first login." },
                { title: "SCIM 2.0 Provisioning", desc: "Automate user lifecycle from your IdP. Create, update, deactivate users and groups without manual administration. Full REST API with OAuth 2.0 for external integrations." },
                { title: "Group-Based Access Control", desc: "Organize users into groups. Assign groups to projects with viewer, editor, or admin access. Ethical walls override group grants." },
              ].map((card, i) => (
                <div key={i} className="bg-white/10 rounded-xl p-6 border border-white/10">
                  <h3 className="text-white font-semibold mb-2" style={{ fontFamily: "system-ui, sans-serif" }}>{card.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed" style={{ fontFamily: "system-ui, sans-serif" }}>{card.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* --- CTA --- */}
      <section id="cta" data-animate className="py-24 bg-[#C8A84B]">
        <div className="max-w-3xl mx-auto px-6">
          <div className={`transition-all duration-700 ${show("cta") ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white rounded-xl border-2 border-[#1B3A6B] p-12 text-center shadow-lg">
              <h2 className="text-3xl md:text-4xl text-[#1B3A6B] mb-4">
                Ready to see what your contracts are really saying?
              </h2>
              <p className="text-gray-500 mb-10 max-w-xl mx-auto" style={{ fontFamily: "system-ui, sans-serif" }}>
                Upload a real contract. See clause-by-clause analysis. Review every finding. Generate a revised document. All in under 5 minutes.
              </p>
              <Link
                href="/demo"
                className="inline-block px-10 py-4 text-sm font-semibold bg-[#1B3A6B] text-white rounded hover:bg-white hover:text-[#1B3A6B] hover:ring-2 hover:ring-[#1B3A6B] transition-all"
                style={{ fontFamily: "system-ui, sans-serif" }}
              >
                Request a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
